import { UsimOrderItemData } from "../../../api/usim/orders/UsimOrderItem";
import { formatDateISO } from "../../../utils/date";
import { fromUSimStatus } from "./usimUtils";

export type TableColumnTag = "pending" | "completed" | "all";

export interface TableColumn {
  title: string;
  dataIndex: string;
  key: string;
  tag?: TableColumnTag;
  render?: (text: string, record: UsimOrderItemData) => React.ReactNode;
  onCell?: (record: UsimOrderItemData) => { rowSpan: number };
  sorter?: (a: any, b: any) => number;
}

// Common column definitions with tag-based filtering
export const getCommonColumns = (tag?: TableColumnTag): TableColumn[] => {
  const allColumns: TableColumn[] = [
    {
      title: "Order Processed Date",
      dataIndex: "orderProcessedDate",
      key: "orderProcessedDate",
      tag: "all",
      render: (text: string, record: UsimOrderItemData) => 
        record.orderProcessedDate ? formatDateISO(record.orderProcessedDate) : "-",
      onCell: (record: UsimOrderItemData) => ({ rowSpan: record._rowSpan ?? 1 }),
      sorter: (a: UsimOrderItemData, b: UsimOrderItemData) => a.orderProcessedDate.localeCompare(b.orderProcessedDate)
    },
    {
      title: "Order Number",
      dataIndex: "OrderNumber",
      key: "OrderNumber",
      tag: "all",
      onCell: (record: UsimOrderItemData) => ({ rowSpan: record._rowSpan ?? 1 }),
      render: (text: string, record: UsimOrderItemData) => 
        record.status?.toUpperCase() === 'CANCELLED' ? (
          `<div style={{ backgroundColor: 'red', color: 'white', padding: '4px', borderRadius: '4px' }}>
         {record.OrderNumber || "-"}
          </div>
        `) : record.OrderNumber || "-"
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      tag: "all",
    },
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
      tag: "all",
    },
    {
      title: "ICCID/SN",
      dataIndex: "iccid",
      key: "iccid",
      tag: "all",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      tag: "all",
      render: (text: string, record: UsimOrderItemData) => fromUSimStatus(record.status),
    },
    {
      title: "Activation Date",
      dataIndex: "activationDate",
      key: "activationDate",
      tag: "all",
      render: (text: string, record: UsimOrderItemData) => 
        record.activationDate ? formatDateISO(record.activationDate) : "-"
    },
    {
      title: "Processed By",
      dataIndex: "processedBy",
      key: "processedBy",
      tag: "completed",
      render: (text: string, record: UsimOrderItemData) => record.processedBy || "-"
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      tag: "completed",
      render: (text: string, record: UsimOrderItemData) => formatDateISO(record.createdAt || "-")
    }
  ];

  return tag ? allColumns.filter(col => col.tag === tag || col.tag === "all") : allColumns;
};

// Group orders by OrderNumber and compute rowSpans
export const groupOrdersByOrderNumber = (data: UsimOrderItemData[]): UsimOrderItemData[] => {
  const groupMap = new Map<string, UsimOrderItemData[]>();
  
  // Group by OrderNumber
  data.forEach((item) => {
    const key = item.OrderNumber;
    const arr = groupMap.get(key) || [];
    arr.push(item);
    groupMap.set(key, arr);
  });

  // Flatten groups with rowSpan
  const flattened: UsimOrderItemData[] = [];
  groupMap.forEach((items) => {
    const count = items.length;
    items.forEach((item, idx) => {
      flattened.push({
        ...item,
        _rowSpan: idx === 0 ? count : 0,
      });
    });
  });

  return flattened;
};

// Common search functionality
export const searchOrders = (data: UsimOrderItemData[], searchQuery: string): UsimOrderItemData[] => {
  if (!searchQuery) {
    return data;
  }

  return data.filter((item: UsimOrderItemData) => {
    const searchString = JSON.stringify({
      orderNumber: item.OrderNumber,
      planName: item.planName,
      type: item.type,
      platform: item.orderData?.MarketplacePlatform,
      email: item.orderData?.OrderDetails?.email,
      status: item.status,
      createdAt: item.createdAt,
      updatedAt: item.updated_at || item.updatedAt,
      iccid: item.iccid,
      mobileNumber: item.mobileNumber
    }).toLowerCase();

    return searchString.includes(searchQuery.toLowerCase());
  });
};

// Export column mapping
export const exportColumnMapping = {
  OrderNumber: "Order Number",
  orderProcessedDate: "Order Processed Date",
  type: "Product Type",
  sku: "SKU",
  iccid: "ICCID/SN",
  status: "Status",
  activationDate: "Activation Date",
  mobileNumber: "Mobile Number",
  ShopId: "Shop ID",
  createdAt: "Created At",
  updatedAt: "Updated At",
  processedBy: "Processed By"
};

// Export data formatter
export const formatOrdersForExport = (data: UsimOrderItemData[]) => {
  return data.map(item => ({
    [exportColumnMapping.OrderNumber]: item.OrderNumber || "",
    [exportColumnMapping.orderProcessedDate]: item.orderProcessedDate ? formatDateISO(item.orderProcessedDate) : "",
    [exportColumnMapping.type]: item.type || "",
    [exportColumnMapping.sku]: item.sku || "",
    [exportColumnMapping.iccid]: item.iccid || "",
    [exportColumnMapping.status]: fromUSimStatus(item.status) || "",
    [exportColumnMapping.activationDate]: item.activationDate ? formatDateISO(item.activationDate) : "",
    [exportColumnMapping.mobileNumber]: item.mobileNumber || "",
    [exportColumnMapping.ShopId]: item.shopId || "",
    [exportColumnMapping.createdAt]: item.createdAt ? formatDateISO(item.createdAt) : "",
    [exportColumnMapping.updatedAt]: item.updatedAt ? formatDateISO(item.updatedAt) : "",
    [exportColumnMapping.processedBy]: item.processedBy || ""
  }));
};

// Export to CSV helper
export const exportToCSV = (data: UsimOrderItemData[]) => {
  if (data.length === 0) {
    throw new Error("No data to export");
  }

  const exportData = formatOrdersForExport(data);
  const headers = Object.values(exportColumnMapping);
  
  const csvContent = [
    headers.join(","),
    ...exportData.map(row => 
      headers.map(header => {
        const value = row[header] || "";
        return value.toString().includes(",") ? `"${value}"` : value;
      }).join(",")
    )
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  
  link.setAttribute("href", url);
  link.setAttribute("download", `orders-export-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = "hidden";
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


